import { ErrorMessage } from '@hookform/error-message';
import i18next from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdClose, MdModeEdit } from "react-icons/md";
import { useCurrentUserLogin } from '../hooks/useCurrentUserLogin';
import { useNavigate } from 'react-router-dom';
import Select, { components, MenuListProps } from 'react-select';
import { toast } from 'react-toastify';
import { useInfiniteTags } from '../api/hooks/useInfiniteTags';
import { useAllCollaborators, useCategories, useDeleteRiskTheme, useGetCurrentUserTags, useSubcategories, useUpdateRiskTheme } from '../api/hooks/useRiskThemes';
import { useAdmin } from '../context/adminContext';
import { useDesignSystem } from '../context/designSystemContext';
import { customMultiSelectStyles, customStyles } from '../styles/MultiSelectStyles';
import { Categories } from '../types/categories';
import { Tag } from '../types/tag';
import { showErrorToast } from '../utils/errorToast';
import { mergeTagOptions, TagOption } from '../utils/tagSelectOptions';
import { transformValue2Back, transformValue2Front } from '../utils/transformValue';

type editRiskButtonType = {
    riskTheme: Categories,
    refetchRiskTheme: () => void
}

export const EditRiskButton: React.FC<editRiskButtonType> = ({ riskTheme, refetchRiskTheme }) => {
    const [editControl, setEditControl] = useState<boolean>(false);
    const [tagsSearch, setTagsSearch] = useState("");
    const navigate = useNavigate(); // usado para redirecionar pagina
    const isAdmin = useAdmin();
    const isAdminUser = Boolean(isAdmin);
    const hasResetRef = useRef(false);

    const { designSystem } = useDesignSystem();

    // Verifica se o usuário atual é o owner do risco
    const currentUserLogin = useCurrentUserLogin();
    const isOwner = riskTheme.owner.login.toLowerCase() === currentUserLogin.toLowerCase();
    const canEditManagers = isAdmin || isOwner;

    const severityOptions = [i18next.t("severity_options_low"), i18next.t("severity_options_medium"), i18next.t("severity_options_high")];
    const ProbabilityOptions = [i18next.t("probability_options_low"), i18next.t("probability_options_medium"), i18next.t("probability_options_high")];

    const clickModal = () => {
        hasResetRef.current = false;
        setEditControl(!editControl)
    };

    // Função para impedir a propagação do clique
    const stopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const { error: ownerError, data: ownerData } = useAllCollaborators();
    const { data: categories } = useCategories(riskTheme.id);
    const { data: subcategories } = useSubcategories(riskTheme.id);
    const { data: currentUserTags, isLoading: currentUserTagsLoading } =
        useGetCurrentUserTags();
    const {
        tags: infiniteTags,
        isLoading: infiniteTagsLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteTags({ enabled: editControl && isAdminUser, search: tagsSearch, pageSize: 20 });

    const availableTags: Tag[] = isAdminUser
        ? infiniteTags ?? []
        : currentUserTags?.adminTags ?? [];

    const fetchNextTagsPage = () => {
        if (!isAdminUser) {
            return;
        }

        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const InfiniteMenuList = (props: MenuListProps<TagOption, true>) => {
        const { innerProps } = props;

        return (
            <components.MenuList
                {...props}
                innerProps={{
                    ...innerProps,
                    onScroll: (event) => {
                        innerProps?.onScroll?.(event);

                        const target = event.currentTarget;
                        const reachedBottom =
                            target.scrollHeight - target.scrollTop - target.clientHeight <= 2;

                        if (reachedBottom) {
                            fetchNextTagsPage();
                        }
                    },
                }}
            />
        );
    };

    const allCategories = categories?.map((val: string) => ({
        label: val,
        value: val,
    }));

    const allSubcategories = subcategories?.map((val: string) => ({
        label: val,
        value: val,
    }));

    const allCollaborators = ownerData?.map((val: any) => ({
        label: val.login
            .replace(/\b\w/g, (char: string) => char.toUpperCase())
            .split(".")
            .join(" "),
        value: val.id,
    }));

    const { register, handleSubmit, control, formState: { errors }, reset, trigger } = useForm<any>({
        defaultValues: {
            ...riskTheme,
            category: riskTheme.category,
            subcategory: riskTheme.subcategory,
            risk_owner: null,
            severity: transformValue2Front(riskTheme.severity, "severity"),
            probability: transformValue2Front(riskTheme.probability, "probability"),
            risk_managers: (riskTheme as any).managers?.map((manager: any) => ({
                label: manager.login?.replace(/\b\w/g, (char: string) => char.toUpperCase()).split(".").join(" ") || manager.name,
                value: manager.id
            })) || [],
            risk_tags: (riskTheme as any).tags?.map((t: { id: number; name: string }) => ({ label: t.name, value: t.id })) || []
        },
    });

    const { mutate: updateRiskTheme } = useUpdateRiskTheme();

    // Atualiza os valores padrão quando o modal abrir e os dados estiverem disponíveis
    useEffect(() => {
        if (editControl && !hasResetRef.current && allCollaborators && allCollaborators.length > 0 && allCategories && allCategories.length > 0 && allSubcategories && allSubcategories.length > 0 && riskTheme) {
            const currentOwner = allCollaborators.find((collab: any) => collab.value === riskTheme.owner.id);
            reset({
                ...riskTheme,
                category: riskTheme.category,
                subcategory: riskTheme.subcategory,
                risk_owner: currentOwner || null,
                severity: transformValue2Front(riskTheme.severity, "severity"),
                probability: transformValue2Front(riskTheme.probability, "probability"),
                risk_managers: (riskTheme as any).managers?.map((manager: any) => ({
                    label: manager.login?.replace(/\b\w/g, (char: string) => char.toUpperCase()).split(".").join(" ") || manager.name,
                    value: manager.id
                })) || [],
                risk_tags: (riskTheme as any).tags?.map((t: { id: number; name: string }) => ({ label: t.name, value: t.id })) || []
            });
            hasResetRef.current = true;
        }
    }, [editControl, allCollaborators, allCategories, allSubcategories, riskTheme, reset]);

    const onSubmit = (data: any) => {
        const request = {
            id: data.id,
            name: data.name,
            category: data.category,
            subcategory: data.subcategory,
            theme: data.theme,
            year: data.year,
            severity: transformValue2Back(data.severity, "severity"),
            probability: transformValue2Back(data.probability, "probability"),
            ownerId: data.risk_owner?.value || riskTheme.owner.id,
            description: data.description,
            managersIds: data.risk_managers?.map((item: any) => item.value) || [],
            tags: data.risk_tags?.map((item: any) => item.value) || []
        }

        // metodo update control
        updateRiskTheme(request, {
            onSuccess: () => {
                setEditControl(false);
                refetchRiskTheme();
                reset({ ...data });
                toast.success("Risco alterado com sucesso");
            },
            onError: (error) => {
                showErrorToast(error, "Erro: Risco não alterado");
            }
        })
    }

    const { mutate: deleteRiskTheme } = useDeleteRiskTheme();

    const deleteButton = () => {
        if (window.confirm(`${i18next.t("delete_risk_confirmation")}\n${i18next.t("name")}: ${riskTheme.name}`)) {
            // metodo delete
            deleteRiskTheme(riskTheme.id, {
                onSuccess: () => {
                    navigate("/", { replace: true });
                    reset(); // verificar inutilidade
                    setEditControl(false); // inutil
                    toast.success("Risco deletado com sucesso");
                },
                onError: (error) => {
                    showErrorToast(error, "Erro: Risco não deletado");
                }
            });
        }
    }


    const tailwindDiv = "";
    const tailwindLabels =
        "text-[#191919] text-[12px] leading-[12px] tracking-[0.1px] font-openSans font-bold mb-2";
    const tailwindInputs =
        "w-full border border-gray-300 rounded-md py-1 pl-2 text-[#191919] text-[14px] tracking-[0.18px] font-openSans";
    const tailwindErrorMessage =
        "text-red-500 text-[12px] leading-[16px] tracking-[0.18px] absolute";

    return (
        <div>
            <MdModeEdit onClick={clickModal} />

            {editControl ?
                <div className="fixed inset-0 bg-black bg-opacity-15 z-50 grid justify-items-end" onClick={() => { setEditControl(false); reset() }}>
                    <div className="fixed top-1/2 right-1/2 bg-white border rounded-xl h-auto max-h-[90vh] overflow-y-auto w-[800px] -translate-y-1/2 translate-x-1/2 z-60 px-5 py-4 " onClick={stopPropagation}>

                        {/* Ícone de fechar no canto superior direito */}
                        <div className="absolute top-2 right-2">
                            <MdClose className="cursor-pointer text-red-500" size={24} onClick={() => setEditControl(false)} />
                        </div>

                        <div className="font-openSans font-bold text-sextenary text-[16px] leading-[16px] tracking-[0.18px] mb-4">
                            {i18next.t("risk_editor")}
                        </div>

                        <form className="text-black" onSubmit={handleSubmit(onSubmit)}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className={tailwindDiv}>
                                    <label className={tailwindLabels}>{i18next.t("risk_name")}</label>
                                    <input
                                        className={tailwindInputs}
                                        type="text"
                                        placeholder="Nome"
                                        {...register("name", {
                                            required: i18next.t("required_field"),
                                            minLength: {
                                                value: 4,
                                                message: i18next.t("min_3_characters_field"),
                                            },
                                        })}
                                    />
                                    <ErrorMessage
                                        name="name"
                                        errors={errors}
                                        render={({ message }) => (
                                            <p className={tailwindErrorMessage}>{message}</p>
                                        )}
                                    />
                                </div>

                                <div className={tailwindDiv}>
                                    <label className={tailwindLabels}>{i18next.t("category")}</label>
                                    <Controller
                                        name="category"
                                        control={control}
                                        rules={{ required: i18next.t("required_field") }}
                                        render={({ field: { onChange, value } }) => (
                                            <Select
                                                options={allCategories}
                                                placeholder={i18next.t("category")}
                                                classNamePrefix="react-select"
                                                isClearable={true}
                                                isSearchable={true}
                                                styles={customStyles}
                                                onChange={selectedOption => {
                                                    onChange(selectedOption ? selectedOption.value : "");
                                                    trigger("category");
                                                }}
                                                value={
                                                    allCategories?.find(
                                                        (option: any) => option.value === value
                                                    ) || (value ? { value, label: value } : null)
                                                }
                                            />
                                        )}
                                    />

                                    <ErrorMessage
                                        name="category"
                                        errors={errors}
                                        render={({ message }) => (
                                            <p className={tailwindErrorMessage}>{message}</p>
                                        )}
                                    />
                                </div>

                                <div className={tailwindDiv}>
                                    <label className={tailwindLabels}>
                                        {i18next.t("subcategory")}
                                    </label>
                                    <Controller
                                        name="subcategory"
                                        control={control}
                                        rules={{ required: i18next.t("required_field") }}
                                        render={({ field: { onChange, value } }) => (
                                            <Select
                                                options={allSubcategories}
                                                placeholder={i18next.t("subcategory")}
                                                classNamePrefix="react-select"
                                                isClearable={true}
                                                isSearchable={true}
                                                styles={customStyles}
                                                onChange={selectedOption => {
                                                    onChange(selectedOption ? selectedOption.value : "");
                                                    trigger("subcategory");
                                                }}
                                                value={
                                                    allSubcategories?.find(
                                                        (option: any) => option.value === value
                                                    ) || (value ? { value, label: value } : null)
                                                }
                                            />
                                        )}
                                    />
                                    <ErrorMessage
                                        name="subcategory"
                                        errors={errors}
                                        render={({ message }) => (
                                            <p className={tailwindErrorMessage}>{message}</p>
                                        )}
                                    />
                                </div>

                                <div className={tailwindDiv}>
                                    <label className={tailwindLabels}>
                                        {i18next.t("risk_theme")}
                                    </label>
                                    <input
                                        className={tailwindInputs}
                                        type="text"
                                        placeholder={i18next.t("risk_theme")}
                                        {...register("theme", {
                                            required: i18next.t("required_field"),
                                        })}
                                    />
                                    <ErrorMessage
                                        name="theme"
                                        errors={errors}
                                        render={({ message }) => (
                                            <p className={tailwindErrorMessage}>{message}</p>
                                        )}
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div className={tailwindDiv}>
                                        <label className={tailwindLabels}>
                                            {i18next.t("severity")}
                                        </label>
                                        <select
                                            className={tailwindInputs}
                                            {...register("severity", {
                                                required: i18next.t("required_field"),
                                            })}
                                        >
                                            {severityOptions.map((option, index) => (
                                                <option key={index} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                        <ErrorMessage
                                            name="severity"
                                            errors={errors}
                                            render={({ message }) => (
                                                <p className={tailwindErrorMessage}>{message}</p>
                                            )}
                                        />
                                    </div>

                                    <div className={tailwindDiv}>
                                        <label className={tailwindLabels}>
                                            {i18next.t("probability")}
                                        </label>
                                        <select
                                            className={tailwindInputs}
                                            {...register("probability", {
                                                required: i18next.t("required_field"),
                                            })}
                                        >
                                            {ProbabilityOptions.map((option, index) => (
                                                <option key={index} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                        <ErrorMessage
                                            name="probability"
                                            errors={errors}
                                            render={({ message }) => (
                                                <p className={tailwindErrorMessage}>{message}</p>
                                            )}
                                        />
                                    </div>

                                    <div className={tailwindDiv}>
                                        <label className={tailwindLabels}>{i18next.t("year")}</label>
                                        <input
                                            className={tailwindInputs}
                                            type="text"
                                            placeholder={new Date().getFullYear().toString()}
                                            {...register("year", {
                                                required: i18next.t("required_field"),
                                                pattern: {
                                                    value: /^\d{4}$/,
                                                    message: i18next.t("min_4_number_for_year"),
                                                },
                                            })}
                                        />
                                        <ErrorMessage
                                            name="year"
                                            errors={errors}
                                            render={({ message }) => (
                                                <p className={tailwindErrorMessage}>{message}</p>
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className={tailwindDiv}>
                                    <label className={tailwindLabels}>
                                        {i18next.t("risk_owner")}
                                    </label>
                                    <Controller
                                        name="risk_owner"
                                        control={control}
                                        rules={{ required: i18next.t("required_field") }}
                                        render={({ field: { onChange, value } }) => (
                                            <Select
                                                options={allCollaborators}
                                                isClearable={true}
                                                isSearchable={true}
                                                styles={customStyles}
                                                onChange={selectedOption => {
                                                    onChange(selectedOption ? selectedOption : "");
                                                    trigger("risk_owner");
                                                }}
                                                placeholder={i18next.t("risk_owner")}
                                                classNamePrefix="react-select"
                                                value={value || null}
                                            />
                                        )}
                                    />

                                    <ErrorMessage
                                        name="risk_owner"
                                        errors={errors}
                                        render={({ message }) => (
                                            <p className={tailwindErrorMessage}>{message}</p>
                                        )}
                                    />
                                </div>

                                {canEditManagers && (
                                    <div className={tailwindDiv}>
                                        <label className={tailwindLabels}>
                                            {i18next.t("risk_managers")}
                                        </label>
                                        <Controller
                                            name="risk_managers"
                                            control={control}
                                            render={({ field: { onChange, value } }) => (
                                                <Select
                                                    options={allCollaborators}
                                                    isMulti
                                                    isClearable={true}
                                                    isSearchable={true}
                                                    closeMenuOnSelect={false}
                                                    hideSelectedOptions={false}
                                                    styles={customMultiSelectStyles}
                                                    onChange={(selectedOptions: any) => {
                                                        onChange(selectedOptions || []);
                                                        trigger("risk_managers");
                                                    }}
                                                    placeholder={i18next.t("risk_managers")}
                                                    classNamePrefix="react-select"
                                                    value={value || []}
                                                />
                                            )}
                                        />

                                        <ErrorMessage
                                            name="risk_managers"
                                            errors={errors}
                                            render={({ message }) => (
                                                <p className={tailwindErrorMessage}>{message}</p>
                                            )}
                                        />
                                    </div>
                                )}

                                <div className={tailwindDiv}>
                                    <label className={tailwindLabels}>
                                        {i18next.t("risk_tags")}
                                    </label>
                                    <Controller
                                        name="risk_tags"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <Select<TagOption, true>
                                                options={mergeTagOptions({
                                                    loadedTags: availableTags,
                                                    selected: (value || []) as TagOption[],
                                                })}
                                                isMulti
                                                isClearable={true}
                                                isSearchable={true}
                                                closeMenuOnSelect={false}
                                                hideSelectedOptions={false}
                                                components={{ MenuList: InfiniteMenuList }}
                                                styles={customMultiSelectStyles}
                                                onChange={(selectedOptions: any) => onChange(selectedOptions || [])}
                                                onInputChange={(inputValue, meta) => {
                                                    if (isAdminUser && meta.action === "input-change") {
                                                        setTagsSearch(inputValue);
                                                    }

                                                    return inputValue;
                                                }}
                                                onMenuScrollToBottom={fetchNextTagsPage}
                                                isLoading={
                                                    (isAdminUser && (infiniteTagsLoading || isFetchingNextPage)) ||
                                                    (!isAdminUser && currentUserTagsLoading)
                                                }
                                                placeholder={i18next.t("risk_tags_placeholder")}
                                                classNamePrefix="react-select"
                                                value={value || []}
                                            />
                                        )}
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className={tailwindLabels}>
                                        {i18next.t("risk_description")}
                                    </label>
                                    <textarea
                                        className={tailwindInputs}
                                        rows={2}
                                        placeholder={i18next.t("risk_description")}
                                        {...register("description", {
                                            required: i18next.t("required_field"),
                                        })}
                                    />
                                    <ErrorMessage
                                        name="description"
                                        errors={errors}
                                        render={({ message }) => (
                                            <p className={tailwindErrorMessage}>{message}</p>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 mt-6">
                                <div className="flex">
                                    <button
                                        className="px-[70px] text-white min-h-[33px] rounded font-semibold text-[14px] leading-[16.34px] text-center bg-red-500"
                                        onClick={deleteButton}
                                        type="button">
                                        {i18next.t("button_delete")}
                                    </button>
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button
                                        className="px-[70px] text-white min-h-[33px] rounded font-semibold text-[14px] leading-[16.34px] text-center"
                                        style={{
                                            backgroundColor: designSystem?.cssBasics.secondaryColor,
                                        }}
                                        type="submit">
                                        {i18next.t("button_save")}
                                    </button>
                                    <button
                                        className="px-[70px] ml-4 min-h-[33px] rounded border-[2.33px] text-[14px] leading-[12px] tracking-[0.18px] font-bold py-2.5"
                                        style={{
                                            color: designSystem?.cssBasics.secondaryColor,
                                            borderColor: designSystem?.cssBasics.secondaryColor,
                                        }}
                                        type="button"
                                        onClick={() => { setEditControl(false); reset() }}>
                                        {i18next.t("button_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                :
                null}

        </div>
    )
}